// Двупосочната сметка на ред от доставката — чисти функции, без React.
//
// Правилото „Пази въведената сума": редът помни коя стойност е ВЪВЕЛ
// операторът (`typedSum`) — ед. цената (подразбиране, и за старите чернови) или
// сумата нето/бруто. Въведената стойност никога не се пренаписва от сметката;
// другите две се извеждат от нея при всяка промяна на количеството,
// разфасовката или ДДС-а. Записът подава сумата (`netSum`/`grossSum`) и
// бекендът я пази ТОЧНО, а ед. цената (5 знака) е производна — 2500 бр за
// 2.10 € дават 0.00084 и документът остава 2.10.
import {
  formatUnitPrice,
  parseDecimal,
  roundMoney,
} from "../../components/numbers";
import type { DeliveryRowDraft } from "./deliveryWorksheetShared";

// Сумата на реда като текст за полето: 2 знака, половинката нагоре (като BE)
export const formatLineSum = (value: number) => roundMoney(value).toFixed(2);

// Цената „обяснява" сумата, ако к-во × цена дава точно нея до стотинка —
// тогава остава (въведената от човек 1.234 × 3 = 3.70 не става 1.23333).
export const priceExplainsNet = (base: number, price: string, net: number) =>
  price !== "" && roundMoney(base * parseDecimal(price)) === roundMoney(net);

// Брутото е съвместимо с нетото, ако закръглянето нето ↔ бруто в едната или
// другата посока дава него (въведено 2.55 → нето 2.13, а 2.13 × 1.2 = 2.556).
// Същият допуск ползва бекендът.
export const grossFitsNet = (net: number, gross: number, factor: number) =>
  Math.abs(gross - net * factor) <= 0.005 * factor + 1e-9;

const priceForNet = (row: DeliveryRowDraft, base: number, net: number) => {
  if (base <= 0 || priceExplainsNet(base, row.price, net)) return row.price;
  return formatUnitPrice(net / base);
};

// Брутото от нетото — или записаното в документа, докато е съвместимо с
// нетото и ставката (при редакция: въведено бруто не „мърда" със стотинка).
const grossForNet = (row: DeliveryRowDraft, net: number, factor: number) => {
  const saved = row.savedGross;
  if (saved != null && saved !== "" && grossFitsNet(net, parseDecimal(saved), factor))
    return formatLineSum(parseDecimal(saved));
  return formatLineSum(net * factor);
};

// Изважда другите две стойности от въведената. `base` = количеството в базови
// единици, `factor` = 1 + ДДС/100 (1, когато ДДС не се начислява). Мутира реда.
export const recalcDeliveryRow = (
  row: DeliveryRowDraft,
  base: number,
  factor: number,
): void => {
  if (row.typedSum === "net") {
    // изтритата сума изтрива и цената — редът иска стойност, не 0
    if (row.net === "") {
      row.gross = "";
      row.price = "";
      return;
    }
    const net = parseDecimal(row.net);
    row.gross = grossForNet(row, net, factor);
    row.price = priceForNet(row, base, net);
    return;
  }
  if (row.typedSum === "gross") {
    if (row.gross === "") {
      row.net = "";
      row.price = "";
      return;
    }
    const net = roundMoney(parseDecimal(row.gross) / factor);
    row.net = formatLineSum(net);
    row.price = priceForNet(row, base, net);
    return;
  }
  // въведена е ед. цената: сума = к-во × цена
  if (row.qty === "" && row.price === "") {
    row.net = "";
    row.gross = "";
    return;
  }
  const net = roundMoney(base * parseDecimal(row.price));
  row.net = formatLineSum(net);
  row.gross = grossForNet(row, net, factor);
};

// Коя стойност се пази за ред, зареден от записан документ: цената, ако дава
// записаната сума (така е била въведена или изведена без остатък), иначе
// сумата — 2500 бр × 0.00084 = 2.10, но записаните 2.11 трябва да останат 2.11.
export const savedRowTypedSum = (
  base: number,
  price: string,
  savedNet: number,
): DeliveryRowDraft["typedSum"] =>
  priceExplainsNet(base, price, savedNet) ? null : "net";
